import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import ProfessorLayout from "@/components/ProfessorLayout";
import OwnerLayout from "@/components/OwnerLayout";
import { StudentDataProvider } from "@/contexts/StudentDataContext";
import { useAuth } from "@/contexts/AuthContext";
import StudentRoutes from "./StudentRoutes";
import {
  Login, LandingPage, ResetPassword, MasterOnboarding, AcceptInvite,
  AnamneseForm, StudentRegistration, ApproveStudent, VideoDemo,
  NutritionKnowledgeBase, NotFound,
  KyraChat, KyraLogin, KyraMobile, ChatInstall, ChatWelcome,
  Index, StudentProfile, AdminAnamneses, AdminAnamneseDetail,
  ExerciseLibrary, BulkMediaUpload, WorkoutTemplates, ProfessorSettings,
  MasterStaffManagement, BusinessMetrics, Financeiro, NutritionReview,
  NutritionConfigPage, NutritionStudents, NutritionStudentProfile,
  NutritionistSettings,
  OwnerAdmin, OwnerPlans, OwnerLogs, OwnerConfig, OwnerStaffManagement,
  AIUsageDashboard, NutritionRevenue,
} from "./lazyPages";

function SmartRootRoute() {
  const { session, isStaff, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (session && isStaff) {
    return <Navigate to="/dashboard" replace />;
  }

  return <LandingPage />;
}

export default function AnimatedRoutes() {
  const location = useLocation();
  const isStudentRoute = location.pathname.startsWith("/aluno");
  const routeKey = isStudentRoute ? "/aluno" : location.pathname;

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={routeKey}>
        <Route path="/" element={<SmartRootRoute />} />
        <Route path="/login" element={<Login />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/onboarding" element={<MasterOnboarding />} />
        <Route path="/accept-invite" element={<AcceptInvite />} />
        <Route path="/anamnese" element={<AnamneseForm />} />
        <Route path="/cadastro/:slug" element={<StudentRegistration />} />
        <Route path="/aprovar-aluno/:token" element={<ApproveStudent />} />
        <Route path="/video-demo" element={<VideoDemo />} />
        <Route path="/base-conhecimento-nutricao" element={<NutritionKnowledgeBase />} />
        <Route path="/kyra/login" element={<Navigate to="/chat/login" replace />} />
        <Route path="/kyra/app" element={<Navigate to="/chat/app" replace />} />
        <Route path="/kyra" element={<Navigate to="/chat" replace />} />
        <Route path="/kyra/*" element={<Navigate to="/chat" replace />} />
        <Route path="/chat/login" element={<KyraLogin />} />
        <Route path="/chat" element={<KyraChat />} />
        <Route path="/chat/app" element={<KyraMobile />} />
        <Route path="/chat/instalar" element={<ChatInstall />} />
        <Route path="/chat/bem-vindo" element={<ChatWelcome />} />
        <Route path="/chat/venda" element={<Navigate to="/?tab=chat" replace />} />

        <Route path="/aluno/*" element={
          <StudentDataProvider>
            <StudentRoutes />
          </StudentDataProvider>
        } />

        {/* Professor routes */}
        <Route element={<ProtectedRoute><ProfessorLayout /></ProtectedRoute>}>
          <Route path="/dashboard" element={<Index />} />
          <Route path="/student/:id" element={<StudentProfile />} />
          <Route path="/admin/anamneses" element={<AdminAnamneses />} />
          <Route path="/admin/anamneses/:id" element={<AdminAnamneseDetail />} />
          <Route path="/exercicios" element={<ExerciseLibrary />} />
          <Route path="/exercicios/upload" element={<BulkMediaUpload />} />
          <Route path="/templates" element={<WorkoutTemplates />} />
          <Route path="/configuracoes" element={<ProfessorSettings />} />
          <Route path="/equipe" element={<MasterStaffManagement />} />
          <Route path="/metricas" element={<BusinessMetrics />} />
          <Route path="/financeiro" element={<Financeiro />} />
          <Route path="/nutricao-revisao" element={<NutritionReview />} />
          <Route path="/nutricao-config" element={<NutritionConfigPage />} />
          <Route path="/nutricao-alunos" element={<NutritionStudents />} />
          <Route path="/nutricao-aluno/:id" element={<NutritionStudentProfile />} />
          <Route path="/nutricao-config-nutri" element={<NutritionistSettings />} />
        </Route>

        {/* Owner routes */}
        <Route element={<ProtectedRoute><OwnerLayout /></ProtectedRoute>}>
          <Route path="/admin" element={<OwnerAdmin />} />
          <Route path="/admin/planos" element={<OwnerPlans />} />
          <Route path="/admin/acessos" element={<OwnerStaffManagement />} />
          <Route path="/admin/ai-usage" element={<AIUsageDashboard />} />
          <Route path="/admin/logs" element={<OwnerLogs />} />
          <Route path="/admin/config" element={<OwnerConfig />} />
          <Route path="/admin/nutricao-revenue" element={<NutritionRevenue />} />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
    </AnimatePresence>
  );
}
